import {
  PropertyCharacteristicsDto,
  PropertyConstructionPeriod,
  PropertyDpeTechnicalDto,
  PropertyDto,
  PropertyEquipment,
  PropertyHeatingEnergy,
} from '@workspace/data-types';

export class DpeTechnicalUtils {
  /**
   * Mappe la chaîne de période de construction ADEME vers l'énumération PropertyConstructionPeriod.
   */
  static mapAdemeConstructionPeriod(periodStr?: string): PropertyConstructionPeriod | null {
    if (!periodStr) return null;
    const clean = periodStr.toLowerCase().trim();

    if (clean.includes('avant 1850') || clean.includes('< 1850')) {
      return PropertyConstructionPeriod.BEFORE_1850;
    }
    if (clean.includes('1850-1913') || clean.includes('1850 - 1913')) {
      return PropertyConstructionPeriod.PERIOD_1850_1913;
    }
    if (clean.includes('avant 1948') || clean.includes('1914-1947') || clean.includes('1914 - 1947')) {
      return PropertyConstructionPeriod.PERIOD_1914_1947;
    }
    if (clean.includes('1948-1974') || clean.includes('1948 - 1969')) {
      return PropertyConstructionPeriod.PERIOD_1948_1969;
    }
    if (clean.includes('1970-1980') || clean.includes('1975-1977') || clean.includes('1978-1982')) {
      return PropertyConstructionPeriod.PERIOD_1970_1980;
    }
    if (clean.includes('1981-1991') || clean.includes('1983-1988')) {
      return PropertyConstructionPeriod.PERIOD_1981_1991;
    }
    if (clean.includes('1989-2000') || clean.includes('1992-2000')) {
      return PropertyConstructionPeriod.PERIOD_1992_2000;
    }
    if (clean.includes('2001-2005') || clean.includes('2001-2010') || clean.includes('2006-2012')) {
      return PropertyConstructionPeriod.PERIOD_2001_2010;
    }
    if (clean.includes('2011-2018') || clean.includes('2013-2021')) {
      return PropertyConstructionPeriod.PERIOD_2011_2018;
    }
    if (
      clean.includes('après 2021') ||
      clean.includes('depuis 2019') ||
      clean.includes('2022') ||
      clean.includes('2023') ||
      clean.includes('2024')
    ) {
      return PropertyConstructionPeriod.SINCE_2019;
    }

    const yearMatch = clean.match(/\b(18\d{2}|19\d{2}|20\d{2})\b/);
    if (yearMatch) {
      const year = Number.parseInt(yearMatch[1], 10);
      if (year < 1850) return PropertyConstructionPeriod.BEFORE_1850;
      if (year <= 1913) return PropertyConstructionPeriod.PERIOD_1850_1913;
      if (year <= 1947) return PropertyConstructionPeriod.PERIOD_1914_1947;
      if (year <= 1969) return PropertyConstructionPeriod.PERIOD_1948_1969;
      if (year <= 1980) return PropertyConstructionPeriod.PERIOD_1970_1980;
      if (year <= 1991) return PropertyConstructionPeriod.PERIOD_1981_1991;
      if (year <= 2000) return PropertyConstructionPeriod.PERIOD_1992_2000;
      if (year <= 2010) return PropertyConstructionPeriod.PERIOD_2001_2010;
      if (year <= 2018) return PropertyConstructionPeriod.PERIOD_2011_2018;
      return PropertyConstructionPeriod.SINCE_2019;
    }

    return null;
  }

  /**
   * Mappe les énergies de chauffage ADEME vers les PropertyHeatingEnergy de l'application.
   */
  static mapAdemeHeatingEnergies(technical: PropertyDpeTechnicalDto): PropertyHeatingEnergy[] {
    const energies = new Set<PropertyHeatingEnergy>();
    const text =
      `${technical.heatingEnergy || ''} ${technical.heatingGenerator || ''} ${technical.heatingEmitter || ''}`.toLowerCase();

    if (text.includes('électr') || text.includes('electr')) {
      energies.add(PropertyHeatingEnergy.ELECTRICITY);
    }
    if (text.includes('gaz')) {
      energies.add(PropertyHeatingEnergy.GAS);
    }
    if (text.includes('fioul') || text.includes('fuel')) {
      energies.add(PropertyHeatingEnergy.OIL);
    }
    if (text.includes('granul') || text.includes('pellet')) {
      energies.add(PropertyHeatingEnergy.PELLETS);
    } else if (text.includes('bois') || text.includes('bûche')) {
      energies.add(PropertyHeatingEnergy.WOOD);
    }
    if (text.includes('pompe à chaleur') || text.includes('pac')) {
      energies.add(PropertyHeatingEnergy.HEAT_PUMP);
    }
    if (text.includes('géotherm')) {
      energies.add(PropertyHeatingEnergy.GEOTHERMAL);
    }
    if (text.includes('aérotherm')) {
      energies.add(PropertyHeatingEnergy.AEROTHERMAL);
    }
    if (text.includes('solaire')) {
      energies.add(PropertyHeatingEnergy.SOLAR);
    }
    if (text.includes('plancher chauffant') || text.includes('au sol')) {
      energies.add(PropertyHeatingEnergy.UNDERFLOOR);
    }
    if (technical.hasAirConditioning || text.includes('climatisation') || text.includes('réversible')) {
      energies.add(PropertyHeatingEnergy.AIR_CONDITIONING);
    }

    return Array.from(energies);
  }

  /**
   * Enrichit un PropertyDto existant avec les attributs techniques issus du DPE.
   * Ne remplace pas les données déjà explicitement renseignées si l'utilisateur les a modifiées.
   */
  static enrichPropertyFromTechnical(
    property: PropertyDto,
    technical?: PropertyDpeTechnicalDto,
  ): PropertyDto {
    if (!technical) return property;

    const enriched = Object.assign(new PropertyDto(), property);

    // 1. Construction
    enriched.construction = {
      ...(enriched.construction || {}),
    };

    // Période de construction
    if (!enriched.construction.year && technical.constructionPeriod) {
      const mappedPeriod = this.mapAdemeConstructionPeriod(technical.constructionPeriod);
      if (mappedPeriod) {
        enriched.construction.year = mappedPeriod;
      }
    }

    // 2. Caractéristiques & Équipements
    enriched.characteristics = Object.assign(
      new PropertyCharacteristicsDto(),
      enriched.characteristics || {},
    );
    enriched.characteristics.equipments = {
      ...(enriched.characteristics.equipments || {}),
    };

    const currentEquipments = new Set<PropertyEquipment>(enriched.characteristics.equipments.items || []);

    // Climatisation
    if (technical.hasAirConditioning) {
      currentEquipments.add(PropertyEquipment.AIR_CONDITIONING);
    }

    // Ventilation (VMC)
    if (technical.ventilationType) {
      const vent = technical.ventilationType.toLowerCase();
      if (
        vent.includes('vmc') ||
        vent.includes('mécanique') ||
        vent.includes('simple flux') ||
        vent.includes('double flux')
      ) {
        currentEquipments.add(PropertyEquipment.VENTILATION);
      }
    }

    enriched.characteristics.equipments.items = Array.from(currentEquipments);

    return enriched;
  }
}
